export interface Customer {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  contactPerson?: string | null;
  address?: string | null;
  taxId?: string | null;
  parentCustomerId?: string | null;
  isBranch?: boolean;
  defaultPaymentTermDays?: number;
  sendOrderStatusEmails?: boolean;
  customerCategoryId?: string | null;
  customerCategoryName?: string | null;
  customerCategoryColor?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: 'ADMIN' | 'OPERATIONS' | 'PARTNER' | 'ORDER_STAFF' | 'TESTER';
  canManageOrderCosts?: boolean;
  canManagePurchaseInvoices?: boolean;
  canManageSupplierOrders?: boolean;
  canViewCustomerSalePrices?: boolean;
  canEditCustomerSalePrices?: boolean;
  canViewSupplierPurchasePrices?: boolean;
  canEditSupplierPurchasePrices?: boolean;
  canViewSupplierOrderPurchasePrices?: boolean;
  canEditSupplierOrderPurchasePrices?: boolean;
  canViewSalesDocuments?: boolean;
  canViewPurchaseDocuments?: boolean;
  canViewFinancialReports?: boolean;
  canViewDashboardFullKpis?: boolean;
  canViewCustomerPayments?: boolean;
  canViewTransportCosts?: boolean;
  canEditTransportCosts?: boolean;
  canViewMargin?: boolean;
  deletedAt?: string | null;
}

export interface OrderProduct {
  id: string;
  orderId: string;
  productVariantId?: string | null;
  productCode?: string | null;
  productEan?: string | null;
  catalogProductName?: string | null;
  variantName?: string | null;
  variantAttributes?: Record<string, string>;
  variantIsDefault?: boolean;
  catalogProductWidth?: number;
  catalogProductHeight?: number;
  catalogProductLength?: number;
  catalogProductThickness?: number;
  productCategoryName?: string | null;
  productCategoryColor?: string | null;
  productCategoryDimensionDisplayProfile?: string | null;
  name: string;
  quantity: number;
  quantityPcs?: number | null;
  quantityM2?: number | null;
  quantityM3?: number | null;
  unit?: string;
  saleUnit?: import('../utils/commercialQuantity').QuantityUnit;
  saleQuantity?: number;
  conversionUnit?: 'pcs' | 'm2' | 'm3' | null;
  conversionQuantity?: number;
  unitPrice?: number;
  totalPrice?: number;
  currency?: string;
  description?: string;
  cnCode?: string;
  netWeight?: number;
  grossWeight?: number;
  countryOfOrigin?: string;
  width?: number;
  height?: number;
  length?: number;
  thickness?: number;
  // Package-based LDM (logistics) defaults carried from the catalog Product,
  // used to prefill cargo-line packaging on the transport form.
  piecesPerPackage?: number | null;
  packageLength?: number | null;
  packageWidth?: number | null;
  packageStackLayers?: number | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface Cost {
  id: string;
  orderId: string;
  transportId?: string | null;
  category: 'PRODUCT_COST' | 'TRANSPORT' | 'CUSTOMS_AGENCY' | 'BORDER_TAX' | 'MARKETING_BONUS' | 'OTHER';
  amount: number;
  originalCurrency: string;
  originalAmount?: number;
  exchangeRateUsed?: number;
  exchangeRateSource?: string | null;
  exchangeRateTableNumber?: string | null;
  exchangeRateRequestedDate?: string | null;
  exchangeRateEffectiveDate?: string | null;
  exchangeRateIsFallback?: boolean | null;
  supplierId?: string | null;
  purchaseInvoiceNumber?: string | null;
  purchaseInvoiceDate?: string | null;
  description: string;
  addedById: string;
  addedBy?: User | null;
  documentUrl?: string;
  documentName?: string;
  incurredAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface Supplier {
  id: string;
  name: string;
  contactPerson?: string;
  contactPeople?: SupplierContact[] | null;
  address?: string;
  taxId?: string;
  email?: string;
  phone?: string;
  defaultCurrency?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SupplierContact {
  name?: string;
  phone?: string;
  email?: string;
}

export interface Invoice {
  id: string;
  orderId: string;
  number: string;
  type: 'FINAL' | 'PROFORMA' | 'ADVANCE';
  issueDate: string;
  dueDate?: string;
  status: 'DRAFT' | 'ISSUED' | 'PAID' | 'CANCELLED';
  totalAmount: number;
  currency: string;
  pdfUrl?: string;
  createdAt: string;
  updatedAt: string;
}

export interface StatusHistory {
  id: string;
  orderId: string;
  status: OrderStatus;
  notes?: string;
  changedAt: string;
  changedBy?: { id: string; name: string | null } | null;
}

export type OrderMarginHistoryLineChange = 'ADDED' | 'UPDATED' | 'REMOVED';

export interface OrderMarginHistoryLineImage {
  unitPrice: number | null;
  currency: string | null;
  quantity: number | null;
  unit: string | null;
  saleQuantity: number | null;
  saleUnit: string | null;
  totalPrice: number | null;
}

export interface OrderMarginHistoryLine {
  orderProductId: string | null;
  name: string | null;
  change: OrderMarginHistoryLineChange;
  before: OrderMarginHistoryLineImage | null;
  after: OrderMarginHistoryLineImage | null;
}

export interface OrderMarginHistoryDetails {
  lines?: OrderMarginHistoryLine[];
  fromStatus?: string;
  toStatus?: string;
  reason?: string;
}

export interface OrderMarginHistoryEntry {
  id: string;
  orderId: string;
  revenue: number;
  totalCost: number;
  margin: number;
  marginPercent: number;
  reason: string;
  changedAt: string;
  changedBy?: {
    id: string;
    name: string;
    email?: string;
  } | null;
  details?: OrderMarginHistoryDetails | null;
}

export type FinancialCompletenessStatus = 'COMPLETE' | 'PENDING';
export type FinancialCompletenessMissingField =
  | 'CUSTOMER_SALE_PRICES'
  | 'CUSTOMER_ORDER_COSTS'
  | 'SUPPLIER_PURCHASE_PRICES';

export interface FinancialCompleteness {
  status: FinancialCompletenessStatus;
  missing: FinancialCompletenessMissingField[];
  salePricesComplete?: boolean;
  customerOrderCostsComplete?: boolean;
  purchasePricesComplete?: boolean;
}

export const CUSTOMER_ORDER_ACTIVE_STATUS_VALUES = [
  'NEW_INQUIRY',
  'QUOTE_SENT',
  'CONFIRMED',
  'IN_FULFILLMENT',
  'READY_FOR_DISPATCH',
  'IN_DELIVERY',
  'PARTIALLY_DELIVERED',
  'DELIVERED',
  // WP3: closed by the approved sales invoice ("Zamknięte fakturą"); set by the
  // backend only, never offered as a manual pick.
  'INVOICED',
  'ON_HOLD',
  'CANCELLED',
] as const;

export const CUSTOMER_ORDER_STATUS_VALUES = CUSTOMER_ORDER_ACTIVE_STATUS_VALUES;

export type OrderStatus = typeof CUSTOMER_ORDER_STATUS_VALUES[number];

/** Invoice-ledger event kind: a sales invoice (PARTIAL) or a later cost correction. */
export type OrderSettlementKind = 'PARTIAL' | 'CORRECTION';
export type OrderSettlementStatus = 'OPEN' | 'PARTIALLY_SETTLED';

export interface OrderSettlementEntry {
  id: string;
  orderId: string;
  kind: OrderSettlementKind;
  recognizedAt: string;
  revenue: number;
  totalCost: number | null;
  margin: number | null;
  marginPercent: number | null;
  note?: string | null;
  createdById?: string | null;
  createdByName?: string | null;
  createdByEmail?: string | null;
  createdAt?: string;
  correctionOfId?: string | null;
}
export type ActiveCustomerOrderStatus = typeof CUSTOMER_ORDER_ACTIVE_STATUS_VALUES[number];

export interface Order {
  id: string;
  orderNumber: string;
  customerId: string;
  customer?: Customer | null;
  createdById: string;
  createdBy?: User | null;
  status: OrderStatus;
  allowedTransitions?: OrderStatus[];
  /** Backend-computed reasons an operator action is unavailable (see modules/warehouse/blockedActions.ts). */
  blockedActions?: import('../modules/warehouse/blockedActions').WarehouseBlockedAction[];

  // Manual numbering support
  manualOrderNumber?: string;
  useManualNumbering: boolean;
  clientOrderNumber?: string;

  // Production & Logistics
  producer?: string;
  supplierId?: string;
  factoryLocation?: string;
  deliveryLocation?: string;

  // Financial: unknown warehouse cost is not zero.
  warehouseCostPending?: boolean;
  revenue: number;
  totalCost: number | null;
  margin: number | null;
  marginPercent: number | null;
  financialCompleteness?: FinancialCompleteness;
  /** Batch COGS in PLN from WZ release. Present only for canViewMargin roles; undefined otherwise. */
  cogsPLN?: number;
  currency: string;
  exchangeRate?: number;
  exchangeRateSource?: string;
  exchangeRateTableNumber?: string;
  exchangeRateRequestedDate?: string;
  exchangeRateEffectiveDate?: string;

  // Planned margin (captured atomically when the order is confirmed)
  plannedRevenue?: number;
  plannedTotalCost?: number;
  plannedMargin?: number;
  plannedMarginPercent?: number;
  plannedMarginSnapshotAt?: string;

  // Invoice ledger totals (derived from OrderSettlement)
  settledRevenue?: number;
  settledTotalCost?: number;
  settledMargin?: number;
  settledMarginPercent?: number;
  settlementStatus?: OrderSettlementStatus;

  // Invoices
  proformaNumber?: string;
  proformaDate?: string;
  invoiceNumber?: string;
  invoiceDate?: string;

  // Waybill (WZ) fields
  waybillNumber?: string;
  waybillDate?: string;

  // Customer payment tracking
  customerPaymentDueDate?: string;
  customerPaymentStatus?: 'PENDING' | 'PAID';
  customerPaymentPaidAt?: string;
  customerPaymentNotes?: string;

  // Dates
  orderDate: string;
  expectedDelivery?: string;
  actualDelivery?: string;
  estimatedCompletionDate?: string;
  /** WP13: forecast month 'YYYY-MM'; absent = the orderDate month. */
  forecastMonth?: string;
  /** WP13: server-computed forecast month 'YYYY-MM' (chosen, else orderDate month). */
  effectiveForecastMonth?: string;
  /** WP14: "Dostawa" — our delivery (default) or customer self-pickup. */
  deliveryMode?: import('../modules/transports/transportDirection').OrderDeliveryMode;

  // Details
  description: string;
  notes?: string;

  // INTRASTAT fields
  incoterms?: string;
  transportMode?: number;
  deliveryCountry?: string;
  dispatchCountry?: string;
  transactionNature?: number;

  // Quote fields
  isQuote?: boolean;
  quoteValidUntil?: string;
  quoteConvertedAt?: string;

  // Account Manager
  accountManagerId?: string;
  accountManager?: User | null;

  createdAt: string;
  updatedAt: string;

  // Relations
  costs: Cost[];
  products: OrderProduct[];
  statusHistory: StatusHistory[];
}

export interface OrderDisplayNumberSource {
  orderNumber: string;
  useManualNumbering?: boolean | null;
  manualOrderNumber?: string | null;
}

// Helper function to get display order number
export const getDisplayOrderNumber = (order: OrderDisplayNumberSource): string => {
  const manualOrderNumber = typeof order.manualOrderNumber === 'string'
    ? order.manualOrderNumber.trim()
    : '';

  return order.useManualNumbering && manualOrderNumber
    ? manualOrderNumber
    : order.orderNumber;
};

// Form types for creating/updating orders
export interface OrderFormData {
  customerId: string;
  description: string;
  notes?: string;
  products: Array<{
    productVariantId?: string | null;
    name: string;
    quantity: number;
    unit?: string;
    saleUnit?: import('../utils/commercialQuantity').QuantityUnit;
    unitPrice: number;
    description?: string;
    cnCode?: string;
    netWeight?: number;
    grossWeight?: number;
    countryOfOrigin?: string;
    width?: number;
    height?: number;
    length?: number;
    thickness?: number;
  }>;
  expectedDelivery?: string;
  currency: string;
  useManualNumbering?: boolean;
  manualOrderNumber?: string;
  clientOrderNumber?: string;
  producer?: string;
  supplierId?: string;
  factoryLocation?: string;
  deliveryLocation?: string;
}
